/** Runtime Affiliation Editor (Admin-only).
 *
 *  Migrates `SECTION_AFFILIATIONS_CATALOGUE` from a hard-coded Python dict
 *  into a live MongoDB collection so taxonomy edits don't need a code
 *  deploy. Also unlocks city-scoped and taxonomy-scoped filtering (e.g.
 *  Mumbai → JAA, Hyderabad → TFJAA, Chennai → TNFJAA under a
 *  "Junior Artist" taxonomy).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, RefreshCw, Filter, X, MapPin,
} from "lucide-react";

const SECTION_LABEL = {
  advertising_agency: "L2 · Advertising Agency",
  production_house:   "L3 · Production House",
  studio:             "L4 · Studio Facility",
  studio_network:     "L5 · Studio · Network · Streamer",
  event_production:   "L6 · Event Production",
  vendor:             "L7 · Vendor",
  investor:           "L8 · Investor",
  pr_agency:          "L9 · PR Agency",
  journalist:         "L10 · Journalist / Paparazzi",
  casting_agency:     "L11 · Casting Agency",
  talent_management_agency: "L11 · Talent Management Agency",
  talent_model:       "L12 · Talent / Model",
  distributor:        "L13 · Distributor",
  sales_agent:        "L13 · Sales Agent",
  exhibitor:          "L14 · Exhibitor",
};

const EMPTY_ROW = {
  section: "talent_model", code: "", name: "", full_name: "",
  category: "", geo: "in", taxonomy: "", city: "", state: "",
  industry: "", active: true,
};

export default function AdminAffiliations() {
  const { user } = useAuth();
  const [rows, setRows]         = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [section, setSection]   = useState("talent_model");
  const [q, setQ]               = useState("");
  const [taxonomy, setTaxonomy] = useState("");
  const [city, setCity]         = useState("");
  const [editing, setEditing]   = useState(null);   // id or "__new__"
  const [draft, setDraft]       = useState(EMPTY_ROW);

  const isAdmin = user?.is_admin
    || (user?.email && user.email === "admin@callsheet.app");

  const reload = async () => {
    setLoading(true);
    try {
      const params = { include_inactive: true };
      if (section)  params.section  = section;
      if (taxonomy) params.taxonomy = taxonomy;
      if (city)     params.city     = city;
      const [aff, sec] = await Promise.all([
        api.get("/admin/affiliations", { params }),
        api.get("/admin/affiliation-sections"),
      ]);
      setRows(aff.data.items || []);
      setSections(sec.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load affiliations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) reload(); }, [section, taxonomy, city]);

  const filteredRows = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter(r =>
      (r.code || "").toLowerCase().includes(needle) ||
      (r.name || "").toLowerCase().includes(needle) ||
      (r.full_name || "").toLowerCase().includes(needle) ||
      (r.category || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const startNew = () => {
    setDraft({ ...EMPTY_ROW, section });
    setEditing("__new__");
  };

  const startEdit = (row) => {
    setDraft({
      section: row.section, code: row.code, name: row.name,
      full_name: row.full_name, category: row.category,
      geo: row.geo || "in",
      taxonomy: row.taxonomy || "", city: row.city || "",
      state: row.state || "", industry: row.industry || "",
      active: row.active !== false,
      sort_order: row.sort_order,
    });
    setEditing(row.id);
  };

  const save = async () => {
    if (!draft.code || !draft.name || !draft.full_name || !draft.category) {
      toast.error("code, name, full_name, category are required");
      return;
    }
    try {
      if (editing === "__new__") {
        await api.post("/admin/affiliations", draft);
        toast.success(`Added ${draft.code}`);
      } else {
        await api.patch(`/admin/affiliations/${editing}`, draft);
        toast.success(`Updated ${draft.code}`);
      }
      setEditing(null);
      setDraft(EMPTY_ROW);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (row, hard=false) => {
    if (!window.confirm(`${hard ? "PERMANENTLY delete" : "Deactivate"} ${row.code}?`)) return;
    try {
      await api.delete(`/admin/affiliations/${row.id}?hard=${hard}`);
      toast.success(`${hard ? "Deleted" : "Deactivated"} ${row.code}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const activeSection = sections.find(s => s.section === section);
  const patchSection = async (patch) => {
    try {
      await api.patch(`/admin/affiliation-sections/${section}`, patch);
      toast.success("Section updated");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Section update failed");
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-white/60" data-testid="admin-affiliations-forbidden">
          Admin access only.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24" data-testid="admin-affiliations">
      <div className="mx-auto max-w-7xl px-6 pt-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#F5B82E] mb-1">
              Runtime Editor
            </div>
            <h1 className="font-display text-4xl">Affiliation Catalogue</h1>
            <p className="text-white/50 text-sm mt-1">
              Live edit of union bodies per onboarding section. Changes hot-reload immediately —
              no code deploy needed.
            </p>
          </div>
          <Button variant="ghost" onClick={reload} data-testid="admin-affiliations-reload">
            <RefreshCw size={14} className="mr-2" /> Reload
          </Button>
        </div>

        {/* Section picker */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-affiliations-section-picker">
          {Object.keys(SECTION_LABEL).map(k => (
            <button
              key={k}
              onClick={() => setSection(k)}
              data-testid={`section-tab-${k}`}
              className={`px-3 py-1.5 rounded-full border text-[11px] tracking-[0.14em] uppercase transition ${
                section === k
                  ? "bg-[#F5B82E]/20 border-[#F5B82E]/60 text-[#F5B82E]"
                  : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {SECTION_LABEL[k]}
            </button>
          ))}
        </div>

        {/* Section config */}
        {activeSection && (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 mb-4 flex flex-wrap gap-6 items-center">
            <div className="text-[11px] tracking-[0.22em] uppercase text-white/50">Section rules</div>
            <label className="flex items-center gap-2 text-xs">
              <span className="text-white/60">min_required</span>
              <input
                type="number" min={0} max={10}
                defaultValue={activeSection.min_required}
                onBlur={e => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v) && v !== activeSection.min_required) patchSection({ min_required: v });
                }}
                data-testid="section-min-required"
                className="w-16 rounded bg-black/40 border border-white/20 px-2 py-1 text-white text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={!!activeSection.membership_required}
                onChange={e => patchSection({ membership_required: e.target.checked })}
                data-testid="section-membership-required"
              />
              <span className="text-white/60">membership_required</span>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={!!activeSection.allow_not_registered}
                onChange={e => patchSection({ allow_not_registered: e.target.checked })}
                data-testid="section-allow-not-registered"
              />
              <span className="text-white/60">allow_not_registered</span>
            </label>
          </div>
        )}

        {/* Filters + actions */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-white/40" />
            <Input
              placeholder="Search code / name / category"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="admin-affiliations-search"
              className="w-72 bg-black/40 border-white/15 text-white"
            />
          </div>
          <Input
            placeholder="taxonomy filter (e.g. junior_artist)"
            value={taxonomy}
            onChange={e => setTaxonomy(e.target.value)}
            data-testid="admin-affiliations-taxonomy-filter"
            className="w-56 bg-black/40 border-white/15 text-white text-sm"
          />
          <Input
            placeholder="city filter (e.g. Mumbai)"
            value={city}
            onChange={e => setCity(e.target.value)}
            data-testid="admin-affiliations-city-filter"
            className="w-48 bg-black/40 border-white/15 text-white text-sm"
          />
          <div className="flex-1" />
          <Button onClick={startNew} data-testid="admin-affiliations-add" className="bg-[#F5B82E] text-black hover:bg-[#F5B82E]/90">
            <Plus size={14} className="mr-1" /> Add affiliation
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="admin-affiliations-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.14em] text-white/50">
              <tr>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Name / Full name</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Taxonomy</th>
                <th className="text-left px-3 py-2">City · State</th>
                <th className="text-left px-3 py-2">Industry</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-white/40 text-center py-6">Loading…</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={7} className="text-white/40 text-center py-6">No rows</td></tr>
              ) : filteredRows.map(row => (
                <tr key={row.id} data-testid={`row-${row.code}`}
                    className={`border-t border-white/5 hover:bg-white/[0.02] ${!row.active ? "opacity-40" : ""}`}>
                  <td className="px-3 py-2 font-mono text-[#F5B82E]">{row.code}</td>
                  <td className="px-3 py-2">
                    <div>{row.name}</div>
                    <div className="text-white/40 text-xs">{row.full_name}</div>
                  </td>
                  <td className="px-3 py-2 text-white/70">{row.category}</td>
                  <td className="px-3 py-2 text-white/70">{row.taxonomy || "—"}</td>
                  <td className="px-3 py-2 text-white/70">
                    {row.city ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} /> {row.city}{row.state ? ` · ${row.state}` : ""}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-white/60">{row.industry || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => startEdit(row)} data-testid={`edit-${row.code}`}
                      className="text-white/60 hover:text-white text-xs mr-2">Edit</button>
                    <button onClick={() => remove(row, false)} data-testid={`deactivate-${row.code}`}
                      className="text-white/60 hover:text-white text-xs mr-2">
                      {row.active ? "Deactivate" : "Reactivate"}
                    </button>
                    <button onClick={() => remove(row, true)} data-testid={`hard-delete-${row.code}`}
                      className="text-red-400/70 hover:text-red-300 text-xs">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             data-testid="admin-affiliations-modal">
          <div className="w-full max-w-2xl rounded-xl border border-white/10 bg-[#0A0A0A] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">
                {editing === "__new__" ? "New affiliation" : `Edit ${draft.code}`}
              </h2>
              <button onClick={() => setEditing(null)} data-testid="admin-affiliations-modal-close"
                      className="text-white/50 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Section">
                <select value={draft.section} onChange={e => setDraft({...draft, section: e.target.value})}
                        data-testid="modal-section"
                        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1.5 text-white">
                  {Object.keys(SECTION_LABEL).map(k =>
                    <option key={k} value={k}>{SECTION_LABEL[k]}</option>
                  )}
                </select>
              </Field>
              <Field label="Code (unique per section)">
                <Input value={draft.code} onChange={e => setDraft({...draft, code: e.target.value.toUpperCase()})}
                       data-testid="modal-code" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="Short name">
                <Input value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})}
                       data-testid="modal-name" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="Category (display group)">
                <Input value={draft.category} onChange={e => setDraft({...draft, category: e.target.value})}
                       data-testid="modal-category" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="Full name" className="col-span-2">
                <Input value={draft.full_name} onChange={e => setDraft({...draft, full_name: e.target.value})}
                       data-testid="modal-full-name" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="Taxonomy (free-form)">
                <Input value={draft.taxonomy} placeholder="junior_artist, featured_cast, …"
                       onChange={e => setDraft({...draft, taxonomy: e.target.value})}
                       data-testid="modal-taxonomy" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="Geo">
                <Input value={draft.geo} onChange={e => setDraft({...draft, geo: e.target.value})}
                       data-testid="modal-geo" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="City">
                <Input value={draft.city} onChange={e => setDraft({...draft, city: e.target.value})}
                       data-testid="modal-city" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="State (2-letter)">
                <Input value={draft.state}
                       onChange={e => setDraft({...draft, state: e.target.value.toUpperCase()})}
                       data-testid="modal-state" className="bg-black/40 border-white/15 text-white" />
              </Field>
              <Field label="Industry" className="col-span-2">
                <Input value={draft.industry} placeholder="bollywood, tollywood, kollywood…"
                       onChange={e => setDraft({...draft, industry: e.target.value})}
                       data-testid="modal-industry" className="bg-black/40 border-white/15 text-white" />
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <Button variant="ghost" onClick={() => setEditing(null)} data-testid="modal-cancel">Cancel</Button>
              <Button onClick={save} data-testid="modal-save" className="bg-[#F5B82E] text-black hover:bg-[#F5B82E]/90">
                <Save size={14} className="mr-1" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[10px] uppercase tracking-[0.16em] text-white/50">{label}</span>
      {children}
    </label>
  );
}
